// UI strings for the bespoke interactive Sudoku Puzzle Generator widget
// (real playable board: tap cells + number pad, mistakes/timer/pause,
// pencil notes, undo, and a 3-step teaching hint carousel)

export const sudokuTranslations: Record<string, {
    difficultyLabel: string
    easy: string
    medium: string
    hard: string
    newGame: string
    hint: string
    solved: string
    mistakesLabel: string
    timeLabel: string
    gameOver: string
    hintTitleOnlyCandidate: string
    hintTitleDirect: string
    hintStep1: string
    hintStep2LastRemaining: string
    hintStep3LastRemaining: string
    hintStep2OnlyCandidate: string
    hintStep3OnlyCandidate: string
    hintStep2Direct: string
    hintStep3Direct: string
    unitRow: string
    unitColumn: string
    unitBlock: string
    classicView: string
    flagView: string
    allTimeLabel: string
    myFlagStats: string
    last24h: string
    last7d: string
    last30d: string
    noStatsYet: string
    myStatsNote: string
    shareLabel: string
    saveAsAppLabel: string
    likeLabel: string
    linkCopied: string
    installInstructions: string
}> = {
    en: {
        difficultyLabel: 'Difficulty', easy: 'Easy', medium: 'Medium', hard: 'Hard',
        newGame: 'New Game', hint: 'Hint', solved: '🎉 Solved!',
        mistakesLabel: 'Mistakes', timeLabel: 'Time', gameOver: 'Game Over',
        hintTitleOnlyCandidate: 'Only Candidate', hintTitleDirect: 'Hint',
        hintStep1: 'Pay attention to these cells and the highlighted areas',
        hintStep2LastRemaining: 'In {unit}, there is only one cell remaining that can contain {n}',
        hintStep3LastRemaining: 'Since it is the only possible option, this cell must be {n}',
        hintStep2OnlyCandidate: 'In this cell, every number from 1 to 9 except {n} already appears in its row, column, or box',
        hintStep3OnlyCandidate: 'Since {n} is the only number left that could go here, this cell must be {n}',
        hintStep2Direct: "This cell doesn't have a simple pattern yet - based on the full solution, it must be {n}",
        hintStep3Direct: 'Fill it in to keep going',
        unitRow: 'this row', unitColumn: 'this column', unitBlock: 'this block',
        classicView: 'Classic View', flagView: 'Flag View',
        allTimeLabel: 'All Time', myFlagStats: 'My Flag Stats',
        last24h: 'Last 24 Hours', last7d: 'Last 7 Days', last30d: 'Last 30 Days',
        noStatsYet: 'No games played in this period yet.',
        myStatsNote: 'Your stats on this device only - not a global leaderboard yet.',
        shareLabel: 'Share', saveAsAppLabel: 'Save as App', likeLabel: 'Like',
        linkCopied: 'Link copied!',
        installInstructions: "Open your browser menu and tap 'Add to Home Screen'.",
    },
    ru: {
        difficultyLabel: 'Сложность', easy: 'Лёгкий', medium: 'Средний', hard: 'Сложный',
        newGame: 'Новая игра', hint: 'Подсказка', solved: '🎉 Решено!',
        mistakesLabel: 'Ошибки', timeLabel: 'Время', gameOver: 'Игра окончена',
        hintTitleOnlyCandidate: 'Единственный вариант', hintTitleDirect: 'Подсказка',
        hintStep1: 'Обратите внимание на эти клетки и выделенные области',
        hintStep2LastRemaining: 'В {unit} осталась только одна клетка, куда может поместиться {n}',
        hintStep3LastRemaining: 'Так как это единственный возможный вариант, в этой клетке должно быть {n}',
        hintStep2OnlyCandidate: 'В этой клетке все числа от 1 до 9, кроме {n}, уже есть в её строке, столбце или квадрате',
        hintStep3OnlyCandidate: 'Так как {n} - единственное оставшееся число, которое сюда подходит, в этой клетке должно быть {n}',
        hintStep2Direct: 'Для этой клетки пока нет простого правила - по полному решению здесь должно быть {n}',
        hintStep3Direct: 'Впишите число, чтобы продолжить',
        unitRow: 'этой строке', unitColumn: 'этом столбце', unitBlock: 'этом квадрате',
        classicView: 'Классический вид', flagView: 'Вид с флагами',
        allTimeLabel: 'Всего очков', myFlagStats: 'Моя статистика флагов',
        last24h: 'За 24 часа', last7d: 'За 7 дней', last30d: 'За 30 дней',
        noStatsYet: 'За этот период пока нет сыгранных партий.',
        myStatsNote: 'Статистика только на этом устройстве — это ещё не глобальный рейтинг.',
        shareLabel: 'Поделиться', saveAsAppLabel: 'Сохранить как приложение', likeLabel: 'Нравится',
        linkCopied: 'Ссылка скопирована!',
        installInstructions: 'Откройте меню браузера и нажмите «Добавить на экран «Домой»».',
    },
    lv: {
        difficultyLabel: 'Grūtības Pakāpe', easy: 'Viegls', medium: 'Vidējs', hard: 'Grūts',
        newGame: 'Jauna Spēle', hint: 'Padoms', solved: '🎉 Atrisināts!',
        mistakesLabel: 'Kļūdas', timeLabel: 'Laiks', gameOver: 'Spēle Beigusies',
        hintTitleOnlyCandidate: 'Vienīgā Iespēja', hintTitleDirect: 'Padoms',
        hintStep1: 'Pievērsiet uzmanību šīm šūnām un iezīmētajām zonām',
        hintStep2LastRemaining: '{unit} ir palikusi tikai viena šūna, kurā var ievietot {n}',
        hintStep3LastRemaining: 'Tā kā tā ir vienīgā iespēja, šajā šūnā jābūt {n}',
        hintStep2OnlyCandidate: 'Šajā šūnā visi skaitļi no 1 līdz 9, izņemot {n}, jau ir tās rindā, kolonnā vai kvadrātā',
        hintStep3OnlyCandidate: 'Tā kā {n} ir vienīgais atlikušais skaitlis, kas šeit der, šajā šūnā jābūt {n}',
        hintStep2Direct: 'Šai šūnai vēl nav vienkārša parauga - pēc pilnā risinājuma šeit jābūt {n}',
        hintStep3Direct: 'Ierakstiet skaitli, lai turpinātu',
        unitRow: 'Šajā rindā', unitColumn: 'Šajā kolonnā', unitBlock: 'Šajā kvadrātā',
        classicView: 'Klasiskais Skats', flagView: 'Karogu Skats',
        allTimeLabel: 'Kopā', myFlagStats: 'Mana Karogu Statistika',
        last24h: 'Pēdējās 24 Stundas', last7d: 'Pēdējās 7 Dienas', last30d: 'Pēdējās 30 Dienas',
        noStatsYet: 'Šajā periodā vēl nav spēlētu spēļu.',
        myStatsNote: 'Statistika tikai šajā ierīcē — tas vēl nav globāls topa saraksts.',
        shareLabel: 'Kopīgot', saveAsAppLabel: 'Saglabāt kā Lietotni', likeLabel: 'Patīk',
        linkCopied: 'Saite nokopēta!',
        installInstructions: 'Atveriet pārlūka izvēlni un nospiediet "Pievienot sākuma ekrānam".',
    },
    pl: {
        difficultyLabel: 'Poziom Trudności', easy: 'Łatwy', medium: 'Średni', hard: 'Trudny',
        newGame: 'Nowa Gra', hint: 'Podpowiedź', solved: '🎉 Rozwiązane!',
        mistakesLabel: 'Błędy', timeLabel: 'Czas', gameOver: 'Koniec Gry',
        hintTitleOnlyCandidate: 'Jedyna Możliwość', hintTitleDirect: 'Podpowiedź',
        hintStep1: 'Zwróć uwagę na te pola i podświetlone obszary',
        hintStep2LastRemaining: 'W {unit} pozostało tylko jedno pole, w którym może znaleźć się {n}',
        hintStep3LastRemaining: 'Ponieważ to jedyna możliwa opcja, w tym polu musi być {n}',
        hintStep2OnlyCandidate: 'W tym polu wszystkie liczby od 1 do 9 oprócz {n} występują już w jego wierszu, kolumnie lub bloku',
        hintStep3OnlyCandidate: 'Ponieważ {n} to jedyna pozostała liczba, która tu pasuje, w tym polu musi być {n}',
        hintStep2Direct: 'To pole nie ma jeszcze prostego wzorca - zgodnie z pełnym rozwiązaniem musi tu być {n}',
        hintStep3Direct: 'Wpisz liczbę, aby kontynuować',
        unitRow: 'tym wierszu', unitColumn: 'tej kolumnie', unitBlock: 'tym bloku',
        classicView: 'Widok Klasyczny', flagView: 'Widok Flag',
        allTimeLabel: 'Łącznie', myFlagStats: 'Moje Statystyki Flag',
        last24h: 'Ostatnie 24 Godziny', last7d: 'Ostatnie 7 Dni', last30d: 'Ostatnie 30 Dni',
        noStatsYet: 'W tym okresie nie rozegrano jeszcze żadnej gry.',
        myStatsNote: 'Statystyki tylko na tym urządzeniu — to jeszcze nie globalny ranking.',
        shareLabel: 'Udostępnij', saveAsAppLabel: 'Zapisz jako Aplikację', likeLabel: 'Lubię to',
        linkCopied: 'Link skopiowany!',
        installInstructions: 'Otwórz menu przeglądarki i dotknij "Dodaj do ekranu głównego".',
    },
    es: {
        difficultyLabel: 'Dificultad', easy: 'Fácil', medium: 'Medio', hard: 'Difícil',
        newGame: 'Nueva Partida', hint: 'Pista', solved: '🎉 ¡Resuelto!',
        mistakesLabel: 'Errores', timeLabel: 'Tiempo', gameOver: 'Fin del Juego',
        hintTitleOnlyCandidate: 'Única Opción', hintTitleDirect: 'Pista',
        hintStep1: 'Presta atención a estas celdas y las áreas resaltadas',
        hintStep2LastRemaining: 'En {unit} solo queda una celda que puede contener {n}',
        hintStep3LastRemaining: 'Como es la única opción posible, esta celda debe ser {n}',
        hintStep2OnlyCandidate: 'En esta celda, todos los números del 1 al 9 excepto {n} ya aparecen en su fila, columna o bloque',
        hintStep3OnlyCandidate: 'Como {n} es el único número que puede ir aquí, esta celda debe ser {n}',
        hintStep2Direct: 'Esta celda aún no tiene un patrón simple - según la solución completa, debe ser {n}',
        hintStep3Direct: 'Complétala para continuar',
        unitRow: 'esta fila', unitColumn: 'esta columna', unitBlock: 'este bloque',
        classicView: 'Vista Clásica', flagView: 'Vista de Banderas',
        allTimeLabel: 'Total', myFlagStats: 'Mis Estadísticas de Banderas',
        last24h: 'Últimas 24 Horas', last7d: 'Últimos 7 Días', last30d: 'Últimos 30 Días',
        noStatsYet: 'Todavía no hay partidas jugadas en este período.',
        myStatsNote: 'Tus estadísticas solo en este dispositivo — todavía no es una clasificación global.',
        shareLabel: 'Compartir', saveAsAppLabel: 'Guardar como App', likeLabel: 'Me Gusta',
        linkCopied: '¡Enlace copiado!',
        installInstructions: 'Abre el menú de tu navegador y toca "Añadir a pantalla de inicio".',
    },
    fr: {
        difficultyLabel: 'Difficulté', easy: 'Facile', medium: 'Moyen', hard: 'Difficile',
        newGame: 'Nouvelle Partie', hint: 'Indice', solved: '🎉 Résolu !',
        mistakesLabel: 'Erreurs', timeLabel: 'Temps', gameOver: 'Partie Terminée',
        hintTitleOnlyCandidate: 'Seule Option', hintTitleDirect: 'Indice',
        hintStep1: 'Portez attention à ces cases et aux zones surlignées',
        hintStep2LastRemaining: 'Dans {unit}, il ne reste qu\'une seule case pouvant contenir {n}',
        hintStep3LastRemaining: 'Puisque c\'est la seule option possible, cette case doit être {n}',
        hintStep2OnlyCandidate: 'Dans cette case, tous les chiffres de 1 à 9 sauf {n} apparaissent déjà dans sa ligne, sa colonne ou son bloc',
        hintStep3OnlyCandidate: 'Puisque {n} est le seul chiffre restant qui convient ici, cette case doit être {n}',
        hintStep2Direct: "Cette case n'a pas encore de motif simple - d'après la solution complète, elle doit être {n}",
        hintStep3Direct: 'Remplissez-la pour continuer',
        unitRow: 'cette ligne', unitColumn: 'cette colonne', unitBlock: 'ce bloc',
        classicView: 'Vue Classique', flagView: 'Vue des Drapeaux',
        allTimeLabel: 'Total', myFlagStats: 'Mes Statistiques de Drapeaux',
        last24h: 'Dernières 24 Heures', last7d: 'Derniers 7 Jours', last30d: 'Derniers 30 Jours',
        noStatsYet: "Aucune partie jouée sur cette période pour l'instant.",
        myStatsNote: "Vos statistiques sur cet appareil uniquement - pas encore un classement global.",
        shareLabel: 'Partager', saveAsAppLabel: "Enregistrer comme Appli", likeLabel: "J'aime",
        linkCopied: 'Lien copié !',
        installInstructions: 'Ouvrez le menu de votre navigateur et appuyez sur "Ajouter à l\'écran d\'accueil".',
    },
    it: {
        difficultyLabel: 'Difficoltà', easy: 'Facile', medium: 'Medio', hard: 'Difficile',
        newGame: 'Nuova Partita', hint: 'Suggerimento', solved: '🎉 Risolto!',
        mistakesLabel: 'Errori', timeLabel: 'Tempo', gameOver: 'Partita Finita',
        hintTitleOnlyCandidate: 'Unica Opzione', hintTitleDirect: 'Suggerimento',
        hintStep1: 'Presta attenzione a queste celle e alle aree evidenziate',
        hintStep2LastRemaining: 'In {unit} resta solo una cella che può contenere {n}',
        hintStep3LastRemaining: 'Poiché è l\'unica opzione possibile, questa cella deve essere {n}',
        hintStep2OnlyCandidate: 'In questa cella, tutti i numeri da 1 a 9 tranne {n} sono già presenti nella sua riga, colonna o riquadro',
        hintStep3OnlyCandidate: 'Poiché {n} è l\'unico numero rimasto che può andare qui, questa cella deve essere {n}',
        hintStep2Direct: 'Questa cella non ha ancora uno schema semplice - in base alla soluzione completa, deve essere {n}',
        hintStep3Direct: 'Inseriscilo per continuare',
        unitRow: 'questa riga', unitColumn: 'questa colonna', unitBlock: 'questo riquadro',
        classicView: 'Vista Classica', flagView: 'Vista Bandiere',
        allTimeLabel: 'Totale', myFlagStats: 'Le Mie Statistiche Bandiere',
        last24h: 'Ultime 24 Ore', last7d: 'Ultimi 7 Giorni', last30d: 'Ultimi 30 Giorni',
        noStatsYet: 'Nessuna partita giocata in questo periodo.',
        myStatsNote: 'Le tue statistiche solo su questo dispositivo — non ancora una classifica globale.',
        shareLabel: 'Condividi', saveAsAppLabel: "Salva come App", likeLabel: 'Mi Piace',
        linkCopied: 'Link copiato!',
        installInstructions: 'Apri il menu del browser e tocca "Aggiungi a schermata Home".',
    },
    de: {
        difficultyLabel: 'Schwierigkeitsgrad', easy: 'Leicht', medium: 'Mittel', hard: 'Schwer',
        newGame: 'Neues Spiel', hint: 'Tipp', solved: '🎉 Gelöst!',
        mistakesLabel: 'Fehler', timeLabel: 'Zeit', gameOver: 'Spiel Vorbei',
        hintTitleOnlyCandidate: 'Einzige Möglichkeit', hintTitleDirect: 'Tipp',
        hintStep1: 'Achten Sie auf diese Zellen und die markierten Bereiche',
        hintStep2LastRemaining: 'In {unit} gibt es nur noch eine Zelle, die {n} enthalten kann',
        hintStep3LastRemaining: 'Da dies die einzig mögliche Option ist, muss diese Zelle {n} sein',
        hintStep2OnlyCandidate: 'In dieser Zelle kommen bereits alle Zahlen von 1 bis 9 außer {n} in ihrer Zeile, Spalte oder ihrem Block vor',
        hintStep3OnlyCandidate: 'Da {n} die einzige verbleibende Zahl ist, die hierher passt, muss diese Zelle {n} sein',
        hintStep2Direct: 'Für diese Zelle gibt es noch kein einfaches Muster - laut vollständiger Lösung muss sie {n} sein',
        hintStep3Direct: 'Tragen Sie die Zahl ein, um fortzufahren',
        unitRow: 'dieser Zeile', unitColumn: 'dieser Spalte', unitBlock: 'diesem Block',
        classicView: 'Klassische Ansicht', flagView: 'Flaggen-Ansicht',
        allTimeLabel: 'Gesamt', myFlagStats: 'Meine Flaggen-Statistik',
        last24h: 'Letzte 24 Stunden', last7d: 'Letzte 7 Tage', last30d: 'Letzte 30 Tage',
        noStatsYet: 'In diesem Zeitraum wurden noch keine Spiele gespielt.',
        myStatsNote: 'Ihre Statistik nur auf diesem Gerät - noch keine globale Rangliste.',
        shareLabel: 'Teilen', saveAsAppLabel: 'Als App speichern', likeLabel: 'Gefällt mir',
        linkCopied: 'Link kopiert!',
        installInstructions: 'Öffnen Sie das Browser-Menü und tippen Sie auf "Zum Home-Bildschirm".',
    },
}

export function getSudokuTranslations(lang: string) {
    return sudokuTranslations[lang] || sudokuTranslations.en
}
